import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, CTABand, PractitionerCard, Chips } from "@/components/Shared";
import { ConversionHero, CredentialsStrip, FloatingBar, FirstVisitBox } from "@/components/Landing";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getConcern, CONCERN_FAMILIES } from "@/data/concerns";
import { getTeamMember, TREATMENTS } from "@/data/content";
import { pickPrimary, offerForConcern, variantForConcern, fillFaqs, CONCERN_OVERRIDES } from "@/data/landing";
import { concernImage } from "@/data/concernImages";
import NotFound from "@/pages/NotFound";

const TREATMENT_ROUTES = {
  face: ["injectables", "skin-and-laser"],
  "face/jawline-and-jowls": ["injectables", "threads-and-regenerative", "skin-and-laser"],
  skin: ["skin-and-laser", "minor-procedures"],
  hair: ["threads-and-regenerative"],
  body: ["body-and-core-to-floor"],
  "life-stages": ["bridal-and-events", "skin-and-laser"],
};

const Narrow = ({ children, cream = false, testId, wide = false }) => (
  <section className={cream ? "bg-cream" : "bg-warm-white"} data-testid={testId}>
    <div className={`${wide ? "max-w-4xl" : "max-w-[68ch]"} mx-auto px-8 py-16 lg:py-20`}>{children}</div>
  </section>
);

const ConcernPage = () => {
  const { family, slug } = useParams();
  const concern = getConcern(family, slug);
  if (!concern) return <NotFound />;
  const familyInfo = CONCERN_FAMILIES.find((f) => f.slug === family);
  const override = CONCERN_OVERRIDES[`${family}/${slug}`] || {};
  const variant = variantForConcern(family);
  const primary = override.member ? getTeamMember(override.member) : pickPrimary(concern.who);
  const offer = override.offer || offerForConcern(family, slug, primary);
  const heroImage = concernImage(family, slug);
  const faqs = fillFaqs(concern.faqs, concern.who, offer);
  const relatedTreatments = [
    ...(family === "hair" ? [{ to: "/hair", label: "Hair Restoration" }] : []),
    ...(TREATMENT_ROUTES[`${family}/${slug}`] || TREATMENT_ROUTES[family] || [])
      .map((s) => TREATMENTS.find((t) => t.slug === s))
      .filter(Boolean)
      .map((t) => ({ to: `/treatments/${t.slug}`, label: t.title })),
  ];

  return (
    <>
      <SEO
        title={`${concern.title} | Lumière Aesthetics`}
        description={`${concern.standfirst} Honest, doctor-run advice in Cottesloe, WA — what can be done, what can't, and who to see.`}
        ogImage={heroImage.id}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.lumiereaesthetics.com.au/" },
                { "@type": "ListItem", position: 2, name: "Concerns", item: "https://www.lumiereaesthetics.com.au/concerns" },
                { "@type": "ListItem", position: 3, name: familyInfo.title, item: `https://www.lumiereaesthetics.com.au/concerns/${familyInfo.slug}` },
                { "@type": "ListItem", position: 4, name: concern.title, item: `https://www.lumiereaesthetics.com.au/concerns/${familyInfo.slug}/${concern.slug}` },
              ],
            },
          ],
        }}
      />

      <ConversionHero
        eyebrow={`Concerns · ${familyInfo.title}`}
        title={concern.title}
        standfirst={concern.standfirst}
        member={primary}
        variant={variant}
        offer={offer}
        image={heroImage}
        hair={family === "hair"}
        form={false}
        testId="concern-hero"
      />
      <CredentialsStrip ids={concern.who} />

      <Narrow testId="concern-going-on">
        <Reveal>
          <Eyebrow className="mb-4">What's going on</Eyebrow>
          <div className="space-y-5 text-ink/85 leading-[1.8] text-[17px]">
            {concern.going.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </Reveal>
        <Reveal className="mt-12">
          <Eyebrow className="mb-4">Why it happens</Eyebrow>
          <ul className="mt-5 space-y-2.5" data-testid="concern-why">
            {concern.why.map((item, i) => (
              <li key={i} className="flex gap-3 text-ink/85 text-[15px] leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-mist mt-2.5 shrink-0" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </Narrow>

      <Narrow cream wide testId="concern-can-do">
        <Reveal>
          <Eyebrow className="mb-4">What can realistically be done</Eyebrow>
          <div className="space-y-6 mt-6 max-w-3xl">
            {concern.canDo.map((item, i) => (
              <div key={i} className="border-t border-hairline/40 pt-5" data-testid={`can-do-${i}`}>
                <h3 className="text-deep-sea">{item.name}</h3>
                <p className="text-ink/75 text-[15px] mt-2 leading-relaxed">{item.line}</p>
              </div>
            ))}
            <div className="border-t border-hairline/40 pt-5" data-testid="concern-cant">
              <h3 className="text-deep-sea">What can't</h3>
              <p className="text-ink/85 text-[15px] mt-2 leading-relaxed border-l-2 border-gold pl-5">{concern.cant}</p>
            </div>
          </div>
        </Reveal>
      </Narrow>

      <Narrow wide testId="concern-who">
        <Reveal>
          <Eyebrow className="mb-6">Who to see</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
            {concern.who.map((id, i) => (
              <PractitionerCard key={id} member={getTeamMember(id)} index={i} excerpt />
            ))}
          </div>
        </Reveal>
      </Narrow>

      <Narrow cream wide testId="concern-first-visit">
        <Reveal>
          <FirstVisitBox offer={offer} />
        </Reveal>
      </Narrow>

      <Narrow wide testId="concern-faq">
        <Reveal>
          <Eyebrow className="mb-4">Questions</Eyebrow>
          <Accordion type="single" collapsible className="w-full mt-4 max-w-3xl">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-hairline/40" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="faq-q font-normal text-[20px] md:text-[22px] text-left hover:text-gold transition-colors duration-300 py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/75 text-sm leading-[1.8] pb-5">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Narrow>

      <Narrow cream wide testId="concern-treatments">
        <Reveal>
          <Eyebrow className="mb-5">Related treatments</Eyebrow>
          <Chips items={[...relatedTreatments, ...(override.links || [])]} />
          {family === "hair" && (
            <p className="text-ink/75 text-[15px] mt-8 leading-relaxed">
              Considering treatment? Start with a hair assessment — diagnosis first, then the right treatment, which is not always a transplant.{" "}
              <Link to="/hair" className="link-sea font-medium" data-testid="hair-assessment-card-link">Explore hair restoration</Link>
            </p>
          )}
        </Reveal>
      </Narrow>

      <FloatingBar practitioner={primary?.name} hair={family === "hair"} concern={concern.title} />
      <CTABand />
    </>
  );
};

export default ConcernPage;
